"use client";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
} from "chart.js";
import { useEffect, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import { Chart as MixedChart } from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
);

// Register the mixed chart controller

const InflationChart4 = () => {
  const chartRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const [visiblePoints, setVisiblePoints] = useState(0);

  // Constants
  const initialDeposit = 100000;
  const inflation = 0.03;
  const conservativeYield = 0.02;
  const balancedYield = 0.05;
  const dynamicYield = 0.07;
  const years = Array.from({ length: 21 }, (_, i) => i);

  // Add window width effect
  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      let count = 0;
      const interval = setInterval(() => {
        if (count <= years.length) {
          setVisiblePoints(count);
          count++;
        } else {
          clearInterval(interval);
        }
      }, 200); // Increased timing to make the animation more visible

      return () => clearInterval(interval);
    }
  }, [isVisible, years.length]);

  // Calculate values with inflation impact
  const calculateValues = () => {
    return years.map((year) => ({
      inflation: initialDeposit * Math.pow(1 - inflation, year),
      conservative:
        initialDeposit * Math.pow(1 + (conservativeYield - inflation), year),
      balanced:
        initialDeposit * Math.pow(1 + (balancedYield - inflation), year),
      dynamic: initialDeposit * Math.pow(1 + (dynamicYield - inflation), year),
    }));
  };

  const values = calculateValues();
  const data = {
    labels: years,
    datasets: [
      {
        type: "bar",
        label: "Pouze inflace 3%",
        data: values.map((v, index) =>
          index <= visiblePoints ? v.inflation : null,
        ),
        backgroundColor: "rgba(235, 51, 34, 0.7)",
        borderColor: "#EB3322",
        borderWidth: 1,
      },
      {
        type: "line",
        label: "Konzervativní portfolio 2% (po inflaci -1%)",
        data: values.map((v, index) =>
          index <= visiblePoints ? v.conservative : null,
        ),
        borderColor: "#FFC107",
        backgroundColor: "rgba(255, 193, 7, 0.1)",
        tension: 0.3,
        fill: false,
        borderWidth: windowWidth < 768 ? 1.5 : 2,
        pointRadius: windowWidth < 768 ? 1.5 : 3,
        pointHoverRadius: windowWidth < 768 ? 3 : 6,
        pointBackgroundColor: "#FFC107",
        pointBorderColor: "#FFC107",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#FFC107",
      },
      {
        type: "line",
        label: "Vyvážené portfolio 5% (po inflaci +2%)",
        data: values.map((v, index) =>
          index <= visiblePoints ? v.balanced : null,
        ),
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        tension: 0.3,
        fill: false,
        borderWidth: windowWidth < 768 ? 1.5 : 2,
        pointRadius: windowWidth < 768 ? 1.5 : 3,
        pointHoverRadius: windowWidth < 768 ? 3 : 6,
        pointBackgroundColor: "#4CAF50",
        pointBorderColor: "#4CAF50",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#4CAF50",
      },
      {
        type: "line",
        label: "Dynamické portfolio 7% (po inflaci +4%)",
        data: values.map((v, index) =>
          index <= visiblePoints ? v.dynamic : null,
        ),
        borderColor: "#3669C7",
        backgroundColor: "rgba(54, 105, 199, 0.1)",
        borderWidth: windowWidth < 768 ? 1.5 : 2,
        pointRadius: windowWidth < 768 ? 1.5 : 3,
        pointHoverRadius: windowWidth < 768 ? 3 : 6,
        tension: 0.3,
        fill: false,
        pointBackgroundColor: "#3669C7",
        pointBorderColor: "#3669C7",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#3669C7",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true, // Allows chart to adjust height
    aspectRatio: windowWidth < 768 ? 1 : 1.85, // Adjusts width on mobile
    animation: false, // Disable default animation
    animations: {
      tension: {
        duration: 3000,
        easing: "linear",
        from: 0.5,
        to: 0.3,
        loop: true,
      },
    },
    transitions: {
      active: {
        animation: {
          duration: 300,
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#f1f5f9", // slate-100 color for legend labels
          boxWidth: 10, // Smaller box for mobile
          padding: 10, // Reduced padding
        },
      },
      title: {
        display: true,
        text: "Reálná hodnota investic po započtení inflace",
        font: {
          size: 16,
        },
        color: "#f1f5f9", // slate-100 color for title
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${Math.round(context.raw).toLocaleString()} Kč`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: windowWidth >= 768,
          text: "Roky",
          color: "#f1f5f9", // slate-100 color for x-axis title
        },
        ticks: {
          color: "#f1f5f9", // slate-100 color for x-axis ticks
          font: {
            size: windowWidth < 768 ? 10 : 12,
          },
          maxRotation: 45, // Rotate labels for better fit
          autoSkip: true, // Skip labels if needed
          autoSkipPadding: 10,
        },
        grid: {
          color: "rgba(241, 245, 249, 0.1)", // slightly visible grid lines
        },
      },
      y: {
        beginAtZero: true,
        max: 300000,
        ticks: {
          stepSize: windowWidth < 768 ? 100000 : 50000, // Larger steps on mobile
          callback: function (value) {
            return windowWidth < 768
              ? `${(value / 1000).toLocaleString()}k` // Shorter format on mobile
              : `${value.toLocaleString()} Kč`;
          },
          color: "#f1f5f9", // slate-100 color for y-axis ticks
        },
        title: {
          display: windowWidth >= 768,
          text: "Hodnota (Kč)",
          color: "#f1f5f9", // slate-100 color for y-axis title
        },
        grid: {
          color: "rgba(241, 245, 249, 0.1)", // slightly visible grid lines
        },
      },
    },
  };

  return (
    <section
      className="my-12 flex flex-col gap-8 px-6 md:my-24 md:px-10"
      ref={chartRef}
    >
      <h2 className="my:mb-4 text-center text-3xl md:text-6xl">
        Společně proti inflaci
      </h2>
      <Chart type="bar" options={options} data={data} />
    </section>
  );
};

export default InflationChart4;
